/**
 * ======================================================
 * 🚀 INDEX — POINT D’ENTRÉE PRINCIPAL DE L’APPLICATION
 * ======================================================
 *
 * 🎯 RÔLE DE CE FICHIER
 * ------------------------------------------------------
 * - Charger les styles globaux
 * - Initialiser React
 * - Monter l'application dans le DOM
 * - Injecter tous les Context globaux
 *
 * 👉 C'est ici que toute l'architecture racine se décide.
 *
 * ------------------------------------------------------
 * 🧠 ORDRE DES PROVIDERS (TRÈS IMPORTANT)
 * ------------------------------------------------------
 *
 * 1️⃣ AuthProvider
 *    → Gère l'utilisateur Firebase
 *    → Source de vérité de l'authentification
 *
 * 2️⃣ AuthUIProvider
 *    → Gère l'ouverture/fermeture de la modal auth
 *    → Pure logique UI
 *
 * 3️⃣ AppProvider
 *    → Logique métier globale :
 *       - événements
 *       - carte
 *       - sélection
 *       - etc.
 *
 * ------------------------------------------------------
 * ⚠️ RÈGLE ARCHITECTURALE
 * ------------------------------------------------------
 * Un Provider qui dépend d’un autre
 * DOIT être placé en dessous.
 *
 * ------------------------------------------------------
 */

///////////////////////////////////////////////////////////
// 🎨 IMPORTS STYLES GLOBAUX
///////////////////////////////////////////////////////////

import "./styles/global.css";             // Reset global
import "./styles/theme.css";              // Variables design system
import "./styles/surface.css";            // Surfaces UI

///////////////////////////////////////////////////////////
// ⚛️ IMPORTS REACT
///////////////////////////////////////////////////////////

import React from "react";
import { createRoot } from "react-dom/client";
import { mark } from "./utils/perfMarks";

mark("app_boot");

///////////////////////////////////////////////////////////
// 🧠 IMPORT APP PRINCIPALE
///////////////////////////////////////////////////////////

import App from "./App";

///////////////////////////////////////////////////////////
// 🌍 IMPORT CONTEXTS GLOBAUX
///////////////////////////////////////////////////////////

import { AuthProvider } from "./core/auth/AuthContext";
import { AuthUIProvider } from "./core/auth/AuthUIContext";
import { AppProvider } from "./context/AppContext";
import { PanelNoticeProvider } from "./components/panelNotice/context/PanelNoticeContext";
import { PushOptInProvider } from "./features/notifications/usePushOptIn";
import RootErrorBoundary from "./components/RootErrorBoundary";
import { captureEventsDigestClick } from "./features/eventsDigest/eventsDigest";

///////////////////////////////////////////////////////////
// 🏗 INITIALISATION DU ROOT REACT 18
///////////////////////////////////////////////////////////

/**
 * createRoot = nouvelle API React 18
 *
 * Permet :
 * - concurrent rendering
 * - meilleures performances
 * - compatibilité future
 */

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root container introuvable dans index.html");
}

// Clic depuis le mail « Les sorties autour de toi » : jeton lu puis efface
// du fragment avant le premier rendu (docs/events-digest-email-spec.md §12.2).
captureEventsDigestClick();

const root = createRoot(container);

const isCreationPreview = process.env.NODE_ENV === "development" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
  new URLSearchParams(window.location.search).get("preview") === "event-creation";
const EventCreationLocalPreview = React.lazy(() => import("./features/events/create/EventCreationLocalPreview"));
// Apercu local de l'onglet Favoris de l'Agenda (donnees simulees), developpement seulement.
const isAgendaFavoritesPreview = process.env.NODE_ENV === "development" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
  new URLSearchParams(window.location.search).get("preview") === "agenda-favoris";
const AgendaFavoritesLocalPreview = React.lazy(() => import("./features/history/AgendaFavoritesLocalPreview"));
// ADR-016 : apercu local de la diffusion (API simulee), developpement seulement.
const isDiffusionPreview = process.env.NODE_ENV === "development" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
  new URLSearchParams(window.location.search).get("preview") === "event-diffusion";
const EventDiffusionLocalPreview = React.lazy(() => import("./features/events/diffusion/EventDiffusionLocalPreview"));
// ADR-017 : apercu local de la fenetre de position (services simules), developpement seulement.
const isLocationPromptPreview = process.env.NODE_ENV === "development" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
  new URLSearchParams(window.location.search).get("preview") === "location-prompt";
const LocationPromptLocalPreview = React.lazy(() => import("./features/location/LocationPromptLocalPreview"));
// Apercu local du formulaire « Modifier mon identite » (enregistrement simule), developpement seulement.
const isIdentityEditorPreview = process.env.NODE_ENV === "development" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
  new URLSearchParams(window.location.search).get("preview") === "edition-profil";
const IdentityEditorLocalPreview = React.lazy(() => import("./features/profile/IdentityEditorLocalPreview"));

///////////////////////////////////////////////////////////
// 🎬 RENDER PRINCIPAL
///////////////////////////////////////////////////////////

/**
 * React.StrictMode :
 * ------------------------------------------------------
 * - Aide à détecter les problèmes
 * - Double certains appels en dev
 * - N'affecte PAS la prod
 */

root.render(
  isCreationPreview ? <React.Suspense fallback={<p>Chargement du formulaire…</p>}><EventCreationLocalPreview /></React.Suspense> :
  isAgendaFavoritesPreview ? <React.Suspense fallback={<p>Chargement de l'aperçu…</p>}><AgendaFavoritesLocalPreview /></React.Suspense> :
  isDiffusionPreview ? <React.Suspense fallback={<p>Chargement de l'aperçu…</p>}><EventDiffusionLocalPreview /></React.Suspense> :
  isLocationPromptPreview ? <React.Suspense fallback={<p>Chargement de l'aperçu…</p>}><LocationPromptLocalPreview /></React.Suspense> :
  isIdentityEditorPreview ? <React.Suspense fallback={<p>Chargement de l'aperçu…</p>}><IdentityEditorLocalPreview /></React.Suspense> :
  <React.StrictMode>

    <RootErrorBoundary>

    {/* 🔐 1️⃣ AUTHENTIFICATION FIREBASE */}
    <AuthProvider>

      {/* 🎛 2️⃣ LOGIQUE UI AUTH (MODAL) */}
      <AuthUIProvider>

        {/* 🌍 3️⃣ LOGIQUE MÉTIER GLOBALE */}
        <AppProvider>
          <PanelNoticeProvider>
            <PushOptInProvider>
              {/* 🚀 APPLICATION */}
              <App />
            </PushOptInProvider>
          </PanelNoticeProvider>
        </AppProvider>
      </AuthUIProvider>
    </AuthProvider>

    </RootErrorBoundary>

  </React.StrictMode>
);
